import { isOptionalValueCrdtAlgorithm } from './interfaces/CrdtAlgorithmOptionalValue.js';
import { isReferenceToObjectCrdtAlgorithm } from './interfaces/CrdtAlgorithmReferenceToObject.js';
import { isStandardCrdtAlgorithm } from './interfaces/CrdtAlgorithmStandard.js';
import { createMessageBus } from '../message-bus.js';
import { makeSparseArray, sparseMap } from '../util/array.js';
import { iterateObjects } from '../util/iterate-objects.js';
const MessageBus = createMessageBus('diffObjects');
/**
 * Calculates the difference of two objects expressed as transofrmations of children.
 *
 * @param {T} obj1
 * @param {T} obj2
 * @returns {Promise<Map<string, Transformation[]>>}
 */
export async function diffObjects(obj1, obj2) {
    const diffMap = new Map();
    MessageBus.send('log', `diff objects of type ${obj2.$type$}${obj1 === null ? '. obj1 is null.' : ''}`);
    function appendDiff(k, v) {
        if (v.length === 0) {
            return;
        }
        const value = diffMap.get(k);
        if (value === undefined) {
            diffMap.set(k, [...v]);
        }
        else {
            value.push(...v);
        }
    }
    async function standardDiff(args) {
        MessageBus.send('debug', `diff ${args.path} [${args.valueType.type}] with algorithm ${args.crdtAlgorithm.id}`);
        if (!isStandardCrdtAlgorithm(args.crdtAlgorithm)) {
            throw new Error(`Algorithm ${args.crdtAlgorithm.id} is of incorrect type. Expected "Standard" type, found ${args.crdtAlgorithm.algoType}`);
        }
        appendDiff(args.path.concat('#', args.crdtAlgorithm.id), 0 in args.values
            ? await args.crdtAlgorithm.diff(args.values[0], args.values[1], args.valueType)
            : await args.crdtAlgorithm.initialDiff(args.values[1], args.valueType));
    }
    async function optionalValueDiff(args) {
        // Optional is only set for objectProperties, mapEntries are always optional
        const optional = args.optional === undefined ? true : args.optional;
        MessageBus.send('debug', `diff ${args.path} [${args.valueType.type}] with algorithm ${args.crdtAlgorithm.id}`);
        if (!isOptionalValueCrdtAlgorithm(args.crdtAlgorithm)) {
            throw new Error(`Algorithm ${args.crdtAlgorithm.id} is of incorrect type. Expected "OptionalValue" type, found ${args.crdtAlgorithm.algoType}`);
        }
        if (optional) {
            appendDiff(args.path.concat('#', args.crdtAlgorithm.id), 0 in args.values
                ? await args.crdtAlgorithm.diff(args.values[0], args.values[1])
                : await args.crdtAlgorithm.initialDiff(args.values[1]));
            if (args.values[1] === undefined) {
                return 'off';
            }
            else {
                return 'parallel';
            }
        }
        else {
            return 'parallel';
        }
    }
    await iterateObjects(obj1 === null ? makeSparseArray([[1, obj2]]) : [obj1, obj2], {
        string: standardDiff,
        integer: standardDiff,
        number: standardDiff,
        boolean: standardDiff,
        async referenceToObj(args) {
            MessageBus.send('debug', `diff ${args.path} [referenceToObj] with algorithm ${args.crdtAlgorithm.id}`);
            if (isReferenceToObjectCrdtAlgorithm(args.crdtAlgorithm)) {
                if (args.objs === undefined) {
                    throw new Error('Objs is undefined');
                }
                const o = args.objs;
                const values = sparseMap(args.values, (hash, i) => ({ hash, obj: o[i] }));
                appendDiff(args.path.concat('#', args.crdtAlgorithm.id), 0 in values
                    ? await args.crdtAlgorithm.diff(values[0], values[1])
                    : await args.crdtAlgorithm.initialDiff(values[1]));
                return 'parallel';
            }
            else if (isStandardCrdtAlgorithm(args.crdtAlgorithm)) {
                appendDiff(args.path.concat('#', args.crdtAlgorithm.id), 0 in args.values
                    ? await args.crdtAlgorithm.diff(args.values[0], args.values[1], args.valueType)
                    : await args.crdtAlgorithm.initialDiff(args.values[1], args.valueType));
                return 'off';
            }
            else {
                throw new Error(`Algorithm ${args.crdtAlgorithm.id} is of incorrect type. Expected "ReferenceToObject" ot "Standard" type, found ${args.crdtAlgorithm.algoType}`);
            }
        },
        referenceToId: standardDiff,
        referenceToClob: standardDiff,
        referenceToBlob: standardDiff,
        mapEntry: optionalValueDiff,
        bag: standardDiff,
        array: standardDiff,
        set: standardDiff,
        objectProperty: optionalValueDiff,
        stringifiable: standardDiff
    }, {
        iterateChildObjects: true,
        iterateChildIdObjects: false,
        defaultIterationStrategies: {
            bagValues: 'off',
            arrayValues: 'off',
            setValues: 'off'
        }
    });
    MessageBus.send('log', `diff objects of type ${obj2.$type$} done`, [...diffMap]);
    return diffMap;
}
//# sourceMappingURL=diff-objects.js.map